import { Minus, Plus } from "lucide-react";
import { formatCurrency, visibleLocalityDescription } from "@shared/utils";
import type { Locality } from "@/types";

interface LocalityCardProps {
  locality: Locality;
  quantity: number;
  /** Tope ya resuelto por el contenedor: available, el max de la localidad y maxventadigital. */
  max: number;
  /** Otra localidad ya tiene entradas — una localidad por compra (RF-5). */
  locked: boolean;
  onChange: (qty: number) => void;
  /** Localidad numerada (withSeats === 1): abre el mapa de asientos acotado. */
  onPickSeats?: (localityId: number) => void;
}

/**
 * seleccion-localidades-inline RF-2 — una localidad, en fila:
 * precio grande a la izquierda, nombre y descripción al centro, y la celda de
 * acción a sangre del borde derecho (agotado / elegir asiento / cantidad).
 */
export function LocalityCard({
  locality,
  quantity,
  max,
  locked,
  onChange,
  onPickSeats,
}: LocalityCardProps) {
  const soldOut = locality.available <= 0;
  const numbered = locality.withSeats === 1;
  const description = visibleLocalityDescription(locality.name, locality.description);

  return (
    <div
      // Alto FIJO, nunca se estira para alcanzar al panel de resumen: con una sola
      // localidad esa tarjeta crecía hasta el alto del panel y quedaba deforme.
      // Quien se adapta es el panel (ver PurchaseSummary).
      className="flex min-h-[60px] w-full items-stretch overflow-hidden rounded-xl bg-white sm:min-h-[68px]"
    >
      {/* Precio */}
      <div className="flex shrink-0 items-center py-2.5 pl-4 pr-3 sm:pl-5 sm:pr-4">
        <span className="font-semibold text-primary-110 text-lg sm:text-2xl whitespace-nowrap">
          {formatCurrency(locality.price)}
        </span>
      </div>

      {/* Nombre + descripción */}
      <div className="flex flex-1 flex-col justify-center gap-0.5 py-2.5 pr-3 min-w-0">
        <span className="leading-snug break-words text-primary-110 text-sm sm:text-base">
          {locality.name}
        </span>
        {description ? (
          <p className="line-clamp-2 leading-snug break-words uppercase text-neutral-90 text-[11px] sm:text-xs">
            {description}
          </p>
        ) : null}
      </div>

      {/* Acción */}
      {soldOut ? (
        <div className="flex shrink-0 items-center px-3 sm:px-5">
          <span className="font-bold whitespace-nowrap text-[#e11d48] text-xs sm:text-sm">
            AGOTADO
          </span>
        </div>
      ) : numbered ? (
        <button
          type="button"
          disabled={locked}
          onClick={() => onPickSeats?.(Number(locality.id))}
          className={`flex w-24 shrink-0 cursor-pointer items-center justify-center border-none px-2 text-center font-semibold leading-tight text-primary-110 text-xs sm:w-32 sm:text-sm ${
            locked ? "bg-neutral-100 text-neutral-90 cursor-not-allowed" : "bg-secondary-120"
          }`}
        >
          Elegir asiento
        </button>
      ) : quantity > 0 ? (
        <div className="flex shrink-0 items-center justify-center gap-1 bg-secondary-120 px-2 sm:gap-2 sm:px-3">
          <button
            type="button"
            onClick={() => onChange(quantity - 1)}
            aria-label="Quitar una entrada"
            className="flex cursor-pointer items-center justify-center border-none bg-transparent text-primary-110"
          >
            <Minus size={20} />
          </button>
          <span className="min-w-6 text-center font-bold text-primary-110 text-lg">
            {quantity}
          </span>
          <button
            type="button"
            disabled={quantity >= max}
            onClick={() => onChange(quantity + 1)}
            aria-label="Agregar una entrada"
            className="flex cursor-pointer items-center justify-center border-none bg-transparent text-primary-110 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <Plus size={20} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={locked || max <= 0}
          onClick={() => onChange(1)}
          aria-label="Agregar una entrada"
          className={`flex w-14 shrink-0 items-center justify-center border-none sm:w-16 ${
            locked || max <= 0
              ? "bg-neutral-100 text-neutral-90 cursor-not-allowed"
              : "bg-secondary-120 text-primary-110 cursor-pointer"
          }`}
        >
          <Plus size={22} />
        </button>
      )}
    </div>
  );
}
